import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';

/**
 * Diagnostics for the live-class "Join" button.
 *
 * Field reports: some students get a 404 / no browser when joining, others are
 * fine. The variance is driven by what the `/meetings/:id/join` endpoint returns
 * in `redirectTo` — a direct provider URL (zoom.us / whereby.com) opens the
 * system browser fine, while a link back on `my.lingu.com` is re-captured by the
 * app's Universal/App Links and dead-ends in the in-app router (→ 404), and a
 * custom-scheme URL (zoommtg://) can't be opened via `window.location.href`.
 *
 * We record the join attempt so a suspicious `redirectTo` carries the exact URL
 * (and its classification), the OS (via user-agent) and the runtime container
 * (app vs browser). This runs inside the join thunk, i.e. synchronously BEFORE
 * `window.location.href` navigates the WebView away — the captured event is the
 * reliable signal, because that full-page navigation reloads the document and
 * wipes the breadcrumb buffer.
 *
 * NOTE: the in-app 404 that a lingu-host link dead-ends on is a rendered page,
 * not a thrown error, so it is NOT captured as a separate Sentry event. The
 * pre-navigation "suspicious redirectTo" warning is therefore the primary record
 * of a failing join; `captureJoinError` only fires when the /join API itself
 * throws.
 */

interface IRedirectClassification {
  /** redirectTo was returned and non-empty */
  present: boolean;
  /** starts with http(s):// — the only form Capacitor opens externally */
  isHttp: boolean;
  /** no scheme at all → app builds `/meetings${link}` → in-app 404 route */
  isRelative: boolean;
  /** url scheme, e.g. "https", "zoommtg" (null if unparseable) */
  scheme: string | null;
  /** host, e.g. "zoom.us" (null if unparseable) */
  host: string | null;
  /** host is *.lingu.com → Universal/App Link re-capture → 404 loop risk */
  isLinguHost: boolean;
}

function classifyRedirect(
  redirectTo: string | null | undefined
): IRedirectClassification {
  if (!redirectTo) {
    return {
      present: false,
      isHttp: false,
      isRelative: false,
      scheme: null,
      host: null,
      isLinguHost: false
    };
  }

  const isHttp = /^https?:\/\//i.test(redirectTo);

  let host: string | null = null;
  let scheme: string | null = null;

  try {
    const url = new URL(redirectTo);
    // hostname (not host) so an explicit port doesn't break the lingu-host `$` match
    host = url.hostname || null;
    scheme = url.protocol.replace(/:$/, '') || null;
  } catch {
    // relative or malformed — leave host/scheme null
  }

  return {
    present: true,
    isHttp,
    isRelative: !scheme,
    scheme,
    host,
    isLinguHost: !!host && /(^|\.)lingu\.com$/i.test(host)
  };
}

/** True when the redirect is expected to fail opening a browser on native. */
function isSuspicious(c: IRedirectClassification): boolean {
  return !c.present || c.isRelative || c.isLinguHost || !c.isHttp;
}

/**
 * The full link minus any secrets. We keep the whole location
 * (e.g. `https://my.lingu.com/meetings/1312`) because origin + path is the
 * signal we debug, but drop query/fragment — join URLs can carry tokens,
 * signatures or meeting passwords there.
 */
function redactRedirect(redirectTo: string | null | undefined): string | null {
  if (!redirectTo) return null;

  try {
    const url = new URL(redirectTo);
    return `${url.origin}${url.pathname}`;
  } catch {
    // relative or malformed — keep the path, strip query + fragment
    return redirectTo.split(/[?#]/)[0];
  }
}

interface IRuntimeContext {
  /** runtime container: 'ios' | 'android' | 'web' (NOT the build-time bundle tag) */
  platform: string;
  /** true inside the Capacitor app, false in a browser — the real app-vs-browser signal */
  isNative: boolean;
  /** raw UA — Sentry parses it server-side into os/browser, and it carries the OS version */
  userAgent: string;
}

/**
 * Runtime environment captured ON the join event itself. We do NOT rely on the
 * global build-time `app_platform` tag: the failure path navigates the native
 * WebView to a lingu-host URL, which loads the web bundle and would flip that
 * tag to `web`. `Capacitor.getPlatform()` reports the real container instead.
 */
function getRuntimeContext(): IRuntimeContext {
  return {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
  };
}

/**
 * Report a successful join API response (records `redirectTo` + classification).
 *
 * Diagnostics must never break the join flow, so the whole body is isolated in a
 * try/catch — a throwing Sentry call here would otherwise reject the join thunk
 * and leave the user unable to enter a class despite a valid redirect.
 */
export function captureJoinRedirect(meetingId: number, redirectTo: string | null): void {
  try {
    const c = classifyRedirect(redirectTo);

    // Healthy redirect → touch Sentry not at all. Only a redirect that is
    // expected to fail opening a browser is worth recording. A message per
    // healthy Join click (1M+ users) would only burn quota and bury the
    // actionable ones.
    if (!isSuspicious(c)) return;

    const rt = getRuntimeContext();

    // Sent synchronously, BEFORE the caller does `window.location.href`, so the
    // failure-prone redirect (with OS + runtime container) is recorded even
    // though the later navigation tears down the WebView.
    Sentry.captureMessage('meeting.join suspicious redirectTo', {
      level: 'warning',
      tags: {
        feature: 'meeting_join',
        join_redirect_present: String(c.present),
        join_redirect_is_http: String(c.isHttp),
        join_redirect_is_relative: String(c.isRelative),
        join_redirect_is_lingu_host: String(c.isLinguHost),
        join_redirect_scheme: c.scheme ?? 'none',
        join_redirect_host: c.host ?? 'none',
        join_runtime_platform: rt.platform,
        join_is_native: String(rt.isNative)
      },
      extra: { meetingId, redirectTo: redactRedirect(redirectTo), ...c, ...rt }
    });
  } catch {
    // never let diagnostics break the join
  }
}

/**
 * A small, explicitly safe view of a (possibly Axios) error. We never spread the
 * raw error into Sentry `extra`: an Axios error carries `config.headers` (auth
 * token), request/response bodies and circular refs — a leak and serialization
 * hazard. `Sentry.captureException` already records the parsed error itself.
 */
function describeError(error: unknown): Record<string, string | number | null> {
  const e = error as {
    message?: string;
    response?: { status?: number };
    config?: { method?: string; url?: string };
  };

  return {
    err_message: e?.message ?? String(error),
    err_status: e?.response?.status ?? null,
    err_method: e?.config?.method ?? null,
    err_url: e?.config?.url ?? null
  };
}

/** Report a failed join API call (`/meetings/:id/join` threw). */
export function captureJoinError(meetingId: number, error: unknown): void {
  try {
    const rt = getRuntimeContext();

    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        feature: 'meeting_join',
        join_stage: 'api',
        join_runtime_platform: rt.platform,
        join_is_native: String(rt.isNative)
      },
      extra: { meetingId, ...describeError(error), ...rt }
    });
  } catch {
    // never let diagnostics break the join
  }
}
